
import { supabase } from "@/integrations/supabase/client";
import { Lead, LeadStatus } from "@/types/lead";
import { PlaceResult } from "@/types/place";
import { toast } from "@/hooks/use-toast";
import { checkUserLeadQuota, trackLeadImport } from '@/services/subscriptions';
import { enrichLeadWithWebSearch } from '@/services/webSearchService';
import { saveBulkLeadImages } from '@/services/leadImagesService';
import { generateInitialTemplates } from '@/services/communication/autoGenerate';
import { getMultiplePhotoUrls } from './photoService';
import { placeDetails } from './detailsService';

export const importPlaceAsLead = async (place: PlaceResult, searchQuery?: string): Promise<Lead | null> => {
  try {
    console.log('=== IMPORT PLACE AS LEAD ===');
    console.log('Raw place object received:', place);
    console.log('place_id from original place:', place.place_id);
    
    // Check authentication FIRST
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.id) {
      toast({
        title: "Authentication Required",
        description: "You must be logged in to import leads.",
        variant: "destructive",
      });
      return null;
    }

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*, companies!profiles_company_id_fkey(name)')
      .eq('id', user.id)
      .single();
    
    if (profileError || !profile?.company_id) {
      console.error('Error getting user profile or no company_id:', profileError);
      toast({
        title: "Company Required",
        description: profileError ? "Error loading profile data. Please try again." : "Please select an active company before importing leads.",
        variant: "destructive",
      });
      return null;
    }

    // Check quota AFTER auth and company checks
    const hasQuota = await checkUserLeadQuota();
    
    if (!hasQuota) {
      console.log('User has no quota to import leads');
      toast({
        title: "Import Limit Reached",
        description: "You've reached your monthly lead import limit. Upgrade your subscription for more imports.",
        variant: "destructive",
      });
      return null;
    }

    let placeData = place;
    const originalPlaceId = place.place_id;
    
    if (!place.website || !place.international_phone_number) {
      console.log('Fetching additional place details...');
      const details = await placeDetails(place.place_id);
      console.log('Detailed place data:', details);
      
      if (details.status === 'OK') {
        placeData = {
          ...details.result,
          place_id: details.result.place_id || originalPlaceId,
          // Prefer international phone number, fallback to formatted
          international_phone_number: details.result.international_phone_number || details.result.formatted_phone_number
        };
      }
    }

    if (!placeData.place_id) {
      placeData.place_id = originalPlaceId;
    }

    console.log('=== INSERT OPERATION DEBUG ===');
    console.log('Inserting lead with data:', {
      name: placeData.name,
      address: placeData.formatted_address || placeData.vicinity || placeData.address,
      phone: placeData.international_phone_number || placeData.formatted_phone_number,
      website: placeData.website,
      company_id: profile.company_id,
      created_by: user.id,
      category: searchQuery || (placeData.types ? placeData.types[0] : null)
    });

    const { data: newLead, error: insertError } = await supabase
      .from('leads')
      .insert([{
        name: placeData.name,
        address: placeData.formatted_address || placeData.vicinity || placeData.address,
        phone: placeData.international_phone_number || placeData.formatted_phone_number,
        website: placeData.website,
        email: null, // Set email as null initially - it will be JSON now, not a string
        rating: placeData.rating,
        review_count: placeData.user_ratings_total,
        category: searchQuery || (placeData.types ? placeData.types[0] : null),
        place_id: placeData.place_id,
        status: 'new' as LeadStatus,
        is_favorite: false,
        image_url: null, // Will be updated after photo fetch
        latitude: placeData.geometry?.location?.lat || null,
        longitude: placeData.geometry?.location?.lng || null,
        company_id: profile.company_id,
        created_by: user.id
      }])
      .select('id')
      .single();

    if (insertError) {
      console.error('Supabase insert error:', insertError);
      toast({
        title: "Import Failed",
        description: "Failed to import lead. Please try again later.",
        variant: "destructive",
      });
      throw insertError;
    }

    // Immediately set status to pending for real-time UI feedback
    await supabase
      .from('leads')
      .update({
        web_search_status: 'pending',
        web_search_queue_time: new Date().toISOString()
      })
      .eq('id', newLead.id);

    const { data: lead, error: fetchError } = await supabase
      .from('leads')
      .select(`
        id,
        name,
        address,
        category,
        email,
        phone,
        website,
        image_url,
        is_favorite,
        rating,
        review_count,
        status,
        latitude,
        longitude,
        company_id,
        place_id,
        created_by,
        created_at,
        updated_at,
        web_search_data,
        web_search_status,
        web_search_updated_at,
        web_search_queue_time
      `)
      .eq('id', newLead.id)
      .single();

    if (fetchError) {
      console.error('Supabase fetch error:', fetchError);
      toast({
        title: "Import Failed",
        description: "Failed to fetch imported lead. Please try again later.",
        variant: "destructive",
      });
      throw fetchError;
    }

    // Fetch photos AFTER lead is saved (non-blocking)
    let imageUrls: string[] = [];
    let photoWarning: string | null = null;
    
    if (placeData.photos && placeData.photos.length > 0) {
      try {
        console.log(`Fetching ${placeData.photos.length} photos for lead...`);
        imageUrls = await getMultiplePhotoUrls(placeData.photos, 800, 10);
        
        if (imageUrls.length > 0) {
          console.log(`Successfully fetched ${imageUrls.length} photos`);
          
          // Save images to lead_images table
          await saveBulkLeadImages(lead.id, imageUrls);
          
          // Update lead with first image as primary
          const firstImageUrl = imageUrls[0];
          await supabase
            .from('leads')
            .update({ image_url: firstImageUrl })
            .eq('id', lead.id);
            
          console.log(`Updated lead with primary image`);
        } else {
          photoWarning = "No photos could be loaded";
          console.warn(`Photo fetch returned empty array for lead ${lead.id}`);
        }
      } catch (photoError) {
        photoWarning = "Failed to load photos";
        console.warn(`Non-fatal error fetching photos for lead ${lead.id}:`, photoError);
        // Continue with import - photos are non-critical
      }
    }

    const convertedLead: Lead = {
      id: lead.id,
      name: lead.name,
      address: lead.address || '',
      phone: lead.phone || undefined,
      website: lead.website || undefined,
      email: '', // Initialize with empty string to match our updated type
      imageUrl: lead.image_url || undefined,
      rating: lead.rating || undefined,
      reviewCount: lead.review_count || undefined,
      category: lead.category || undefined,
      placeId: lead.place_id || undefined,
      latitude: lead.latitude || undefined,
      longitude: lead.longitude || undefined,
      status: lead.status as LeadStatus || 'new',
      isFavorite: lead.is_favorite || false,
      companyId: lead.company_id || undefined,
      createdAt: new Date(lead.created_at).toISOString(),
      updatedAt: '',
      notes: [],
      documents: [],
      images: imageUrls.map((url, index) => ({
        id: '',
        leadId: lead.id,
        imageUrl: url,
        isPrimary: false,
        position: index
      })),
      // Use actual web_search_data from database instead of stub
      webSearchData: lead.web_search_data || { status: 'pending', linkedIn: null },
      webSearchStatus: lead.web_search_status || 'pending',
      webSearchUpdatedAt: lead.web_search_updated_at || null
    };

    await trackLeadImport();
    
    // Run background tasks without blocking the response
    try {
      // Use self-invoking async function for background tasks
      (async () => {
        try {
          // First run web search enrichment
          await enrichLeadWithWebSearch({
            ...convertedLead,
            webSearchData: { status: 'pending' }
          });
          
          // After web search is complete, generate templates
          console.log("Web search completed, now generating templates for lead:", convertedLead.id);
          await generateInitialTemplates(convertedLead, profile);
          console.log('Templates generated successfully for lead:', convertedLead.id);
        } catch (error) {
          console.error("Error in background tasks:", error);
          
          // Surface error to user with toast
          const { toast } = await import('sonner');
          toast.error('LinkedIn enrichment failed', {
            description: `Failed to enrich "${convertedLead.name}". You can retry manually from the lead details page.`,
            action: {
              label: 'View Lead',
              onClick: () => {
                // Navigate to lead details page
                window.location.href = `/leads/${convertedLead.id}`;
              }
            },
            duration: 10000,
          });
        }
      })().catch(error => {
        console.error("Error in background tasks:", error);
      });
    } catch (error) {
      console.error("Failed to initiate background tasks:", error);
    }
    
    return convertedLead;
  } catch (error) {
    console.error("Error importing place as lead:", error);
    
    // Categorize error for better user feedback
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    
    if (errorMessage.includes('quota') || errorMessage.includes('limit')) {
      toast({
        title: "Import Limit Reached",
        description: "You've reached your monthly import limit.",
        variant: "destructive",
      });
    } else if (errorMessage.includes('auth') || errorMessage.includes('login')) {
      toast({
        title: "Authentication Required",
        description: "Please log in to import leads.",
        variant: "destructive",
      });
    } else if (errorMessage.includes('company')) {
      toast({
        title: "Company Required",
        description: "Please select a company before importing.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Import Failed",
        description: "Failed to import lead. Please try again.",
        variant: "destructive",
      });
    }
    
    throw error;
  }
};
